import type { SupplierSkuDto } from "@dishour/shared";
import { useState } from "react";
import { useCreateSupplierSku, useSupplierSkus, useUpdateSupplierSku } from "../../api/supplierSkus.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";
import { Skeleton } from "../../components/Skeleton.js";
import { SkuDivisibilityFields } from "../../components/SkuDivisibilityFields.js";
import { formatMoney } from "../../lib/format.js";

const EMPTY_FORM = {
  nameKo: "",
  nameEn: "",
  unit: "박스",
  packSize: "",
  aliases: "",
  isDivisible: false,
  qtyStep: "1",
  unitPrice: "",
};

function SkuRow({ sku }: { sku: SupplierSkuDto }) {
  const updateSku = useUpdateSupplierSku();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    nameKo: sku.nameKo,
    packSize: sku.packSize ?? "",
    unitPrice: sku.unitPrice ?? "",
  });

  return (
    <li className="border-b border-stone-200 py-3">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <p className="font-medium">{sku.nameKo}</p>
          <p className="text-sm text-stone-500">
            {sku.unit}
            {sku.packSize ? ` · ${sku.packSize}` : ""} · {sku.unitPrice ? formatMoney(sku.unitPrice) : "가격 미설정"}
            {!sku.isActive ? " · 비활성" : ""}
          </p>
        </div>
        <div className="flex gap-3 text-sm">
          <button type="button" className="text-brand-700 underline" onClick={() => setEditing(!editing)}>
            수정
          </button>
          <button
            type="button"
            className="text-stone-500 underline"
            disabled={updateSku.isPending}
            onClick={() => updateSku.mutate({ id: sku.id, isActive: !sku.isActive })}
          >
            {sku.isActive ? "비활성화" : "활성화"}
          </button>
        </div>
      </div>

      {editing ? (
        <form
          className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            updateSku.mutate(
              { id: sku.id, nameKo: form.nameKo, packSize: form.packSize || null, unitPrice: form.unitPrice },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          <Field label="품목명" htmlFor={`edit-name-${sku.id}`}>
            <input
              id={`edit-name-${sku.id}`}
              required
              className={inputClass}
              value={form.nameKo}
              onChange={(e) => setForm({ ...form, nameKo: e.target.value })}
            />
          </Field>
          <Field label="포장 규격" htmlFor={`edit-pack-${sku.id}`}>
            <input
              id={`edit-pack-${sku.id}`}
              className={inputClass}
              value={form.packSize}
              onChange={(e) => setForm({ ...form, packSize: e.target.value })}
            />
          </Field>
          <Field label="가격" htmlFor={`edit-price-${sku.id}`}>
            <input
              id={`edit-price-${sku.id}`}
              inputMode="decimal"
              required
              className={inputClass}
              value={form.unitPrice}
              onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
            />
          </Field>
          {updateSku.error ? (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {(updateSku.error as Error).message}
            </p>
          ) : null}
          <button type="submit" className={buttonClass} disabled={updateSku.isPending}>
            저장
          </button>
        </form>
      ) : null}
    </li>
  );
}

export function SupplierSkusPage() {
  const skus = useSupplierSkus();
  const createSku = useCreateSupplierSku();
  const [form, setForm] = useState(EMPTY_FORM);

  return (
    <AppShell title="내 카탈로그">
      <details className="mb-6 rounded-xl border border-stone-200 bg-white p-4">
        <summary className="cursor-pointer font-semibold">새 품목 등록</summary>
        <form
          className="mt-4"
          onSubmit={(e) => {
            e.preventDefault();
            createSku.mutate(
              {
                nameKo: form.nameKo,
                nameEn: form.nameEn || null,
                unit: form.unit,
                packSize: form.packSize || null,
                isDivisible: form.isDivisible,
                qtyStep: form.qtyStep,
                unitPrice: form.unitPrice,
                aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
              },
              { onSuccess: () => setForm(EMPTY_FORM) },
            );
          }}
        >
          <Field label="품목명 (한글)" htmlFor="nameKo">
            <input
              id="nameKo"
              required
              className={inputClass}
              value={form.nameKo}
              onChange={(e) => setForm({ ...form, nameKo: e.target.value })}
            />
          </Field>
          <Field label="품목명 (영문)" htmlFor="nameEn">
            <input
              id="nameEn"
              className={inputClass}
              value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
            />
          </Field>
          <Field label="단위 (박스, 개, 팩 …)" htmlFor="unit">
            <input
              id="unit"
              required
              className={inputClass}
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            />
          </Field>
          <Field label="포장 규격 (예: 10lb)" htmlFor="packSize">
            <input
              id="packSize"
              className={inputClass}
              value={form.packSize}
              onChange={(e) => setForm({ ...form, packSize: e.target.value })}
            />
          </Field>
          <SkuDivisibilityFields
            isDivisible={form.isDivisible}
            qtyStep={form.qtyStep}
            onChange={(next) => setForm({ ...form, ...next })}
          />
          <Field label="가격 (예: 24.00)" htmlFor="unitPrice">
            <input
              id="unitPrice"
              inputMode="decimal"
              required
              className={inputClass}
              value={form.unitPrice}
              onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
            />
          </Field>
          <Field label="다른 표기 (쉼표로 구분: 나빠배추, napa)" htmlFor="aliases">
            <input
              id="aliases"
              className={inputClass}
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
            />
          </Field>
          {createSku.error ? (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {(createSku.error as Error).message}
            </p>
          ) : null}
          <button type="submit" className={buttonClass} disabled={createSku.isPending}>
            품목 추가
          </button>
        </form>
      </details>

      {skus.isPending ? <Skeleton /> : null}
      {skus.data && skus.data.length === 0 ? <EmptyState message="등록된 품목이 없습니다." /> : null}
      <ul>{skus.data?.map((sku) => <SkuRow key={sku.id} sku={sku} />)}</ul>
    </AppShell>
  );
}
